import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import IconProvider from "./IconProvider";

/**
 * EditableTitleComponent - A reusable component for editable titles with configurable header styles
 * 
 * Features:
 * - Click to edit functionality
 * - Configurable header styles (heading-1 through heading-5, with compact variants)
 * - Keyboard support (Enter to save, Escape to cancel)
 * - Blur to save functionality
 * - Hover effects and visual feedback
 * - Disabled state support
 * 
 * Available header styles:
 * - heading-1: Large title (32px)
 * - heading-1-compact: Large title with reduced margins
 * - heading-2: Medium title (24px)
 * - heading-2-compact: Medium title with reduced margins
 * - heading-3: Small title (20px)
 * - heading-3-compact: Small title with reduced margins
 * - heading-4: Extra small title (16px)
 * - heading-4-compact: Extra small title with reduced margins
 * - heading-5: Tiny title (14px)
 */
interface EditableTitleComponentProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  headerStyle?: "heading-1" | "heading-1-compact" | "heading-2" | "heading-2-compact" | "heading-3" | "heading-3-compact" | "heading-4" | "heading-4-compact" | "heading-5";
  className?: string;
  disabled?: boolean;
}

export const EditableTitleComponent = ({
  title,
  onTitleChange,
  headerStyle = "heading-4",
  className,
  disabled = false,
}: EditableTitleComponentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleTitleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleTitleSave = () => {
    if (editValue.trim() && editValue.trim() !== title) {
      onTitleChange(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleTitleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTitleSave();
    } else if (e.key === "Escape") {
      handleTitleCancel();
    }
  };

  // Check if the title looks like a placeholder (Untitled_Template_XXX or Untitled_Object_XXX patterns)
  const isPlaceholderTitle = /^Untitled_(Template|Object)_\d+$/.test(title) || /^Untitled-\d+$/.test(title);

  return (
    <div className={cn("flex-1 min-w-0", className)}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleTitleSave}
          onKeyDown={handleKeyDown}
          className={cn(
            headerStyle,
            "bg-background text-foreground border border-input rounded h-10 px-3 w-full outline-none focus:border-primary transition-colors"
          )}
        />
      ) : (
        <div className="relative group">
          <div 
            className={cn(
              headerStyle,
              !disabled && "cursor-text hover:border-border bg-background rounded h-10 px-3 flex items-center transition-colors",
              isPlaceholderTitle && "text-muted-foreground italic"
            )} 
            onClick={handleTitleClick}
          >
            <span className="flex-1 truncate">{title}</span>
            {!disabled && (
              <IconProvider
                iconstring="fa-pen"
                size={14}
                className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 text-muted-foreground"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
